import CustomFile from 'common/models/CustomFile';
import type { DeepReadonly } from 'ts-essentials'
import { Column, Columns } from './components/Steps/MatchColumns';

export type Meta = { __index: string; __errors?: Error | null }
export type Error = { [key: string]: Info }
export type Errors = { [id: string]: Error }


export enum StepType {
    upload = 'upload',
    selectHeader = 'selectHeader',
    matchColumns = 'matchColumns',
    validateData = 'validateData',
}

// export type StepState =
//     | { type: StepType.upload}
//     | { type: StepType.selectHeader, data: RawData[] }
//     | { type: StepType.matchColumns, data: RawData[], headerValues: RawData, prevData: RawData[] }
//     | { type: StepType.validateData, data: any[], prevData: RawData[], prevHeaderValues: RawData }

export type StepState = {
    step: StepType,
    selectHeader: {
        data: RawData[],
        selectedRow: ReadonlySet<number>
    },
    matchColumns: {
        data: RawData[],
        headerValues?: RawData,
        matchedColumns?: Columns<string>
    },
    validate: {
        data?: any[],
        matchedOptions?: any[],
        matchedColumns?: Columns<string>
    }
}

// export type StepState = {  
//         type: StepType,
//         selectHeaderData?: RawData[],
//         matchColumnsData?: RawData[],
//         matchColumnsHeaderValues?: RawData,
//         validateData?: any[],
//         validateMatchedOptions?: any[],
// }


export type RsiProps<T extends string> = {
    // Is modal visible.
    isOpen: boolean
    // callback when RSI is closed before final submit
    onClose: () => void
    // Field description for requested data
    fields: Fields<T>
    // Runs after file upload step, receives and returns raw sheet data
    uploadStepHook?: (data: RawData[]) => Promise<RawData[]>
    // Runs after header selection step, receives and returns raw sheet data
    selectHeaderStepHook?: (headerValues: RawData, data: RawData[]) => Promise<{ headerValues: RawData; data: RawData[] }>
    // Runs once before validation step, used for data mutations and if you want to change how columns were matched
    matchColumnsStepHook?: (table: Data<T>[], rawData: RawData[], columns: Columns<T>) => Promise<Data<T>[]>
    // Runs after column matching and on entry change
    rowHook?: RowHook<T>
    // Runs after column matching and on entry change
    tableHook?: TableHook<T>
    // Per-row, per-field editability. Cell becomes readonly + empty when this returns false
    isCellEditable?: (row: Data<T>, fieldKey: T) => boolean
    // Tooltip text displayed when hovering a locked (non-editable) cell
    getCellLockedReason?: (row: Data<T>, fieldKey: T) => string | undefined
    // Task #66078: opt-in async server-side validation for the validation step. Called on mount and
    // after edits with the current rows; returns per-line warnings (1-based `line` over the rows).
    // Best-effort — failures are swallowed and never block submit.
    // Task #66220: o `line` de cada resultado TEM de ser a posição (1-based) da linha no array recebido,
    // mesmo que a implementação não envie todas as linhas ao servidor — é por aí que o passo de validação
    // liga cada aviso à sua linha.
    asyncRowValidation?: (rows: Data<T>[]) => Promise<Array<{ line: number; warnings: Array<{ code: string; field: string; message: string }> }>>
    // Task #66078: opt-in leading "#" column in the validation step showing the 1-based row order
    // (the same number used as `line`, so it lines up with server warnings).
    showRowNumber?: boolean
    // Function called after user finishes the flow
    onSubmit: (data: Result<T>, file: File, onSuccess: () => void, onError: () => void) => void
    // Allows submitting with errors. Default: true
    allowInvalidSubmit?: boolean
    // Theme configuration passed to underlying Chakra-UI
    // eslint-disable-next-line @typescript-eslint/ban-types
    customTheme?: object
    // Specifies maximum number of rows for a single import
    maxRecords?: number
    // Maximum upload filesize (in bytes)
    maxFileSize?: number
    // Automatically map imported headers to specified fields if possible. Default: true
    autoMapHeaders?: boolean
    // Headers matching accuracy: 1 for strict and up for more flexible matching
    autoMapDistance?: number
    // Initial Step state to be rendered on load
    initialStepState?: StepState
    // Sets SheetJS dateNF option. If date parsing is applied, date will be formatted e.g. "yyyy-mm-dd hh:mm:ss", "m/d/yy h:mm", 'mmm-yy', etc.
    dateFormat?: string
    // Sets SheetJS "raw" option. If true, parsing will only be applied to xlsx date fields.
    parseRaw?: boolean
    // Use for right-to-left (RTL) support
    rtl?: boolean
}

export type RawData = Array<string | undefined>

export type Data<T extends string> = { [key in T]: string | boolean | undefined }

// Data model RSI uses for spreadsheet imports
export type Fields<T extends string> = DeepReadonly<Field<T>[]>


export type SelectMatch<T extends string> = {
    target: string
    source: T
}


export type Field<T extends string> = {
    // UI-facing field label
    label: string
    // Field's unique identifier
    key: T
    // UI-facing additional information displayed via tooltip and ? icon
    description?: string
    // Alternate labels used for fields' auto-matching, e.g. "fname" -> "firstName"
    alternateMatches?: string[]
    // Validations used for field entries
    validations?: Validation[]
    // Field entry component, default: Input
    fieldType: Checkbox | Select | Input
    // UI-facing values shown to user as field examples pre-upload phase
    example?: string
    history?: boolean
    onlyOneOption?: boolean
}

export type Checkbox = {
    type: 'checkbox'
    // Alternate values to be treated as booleans, e.g. {yes: true, no: false}
    booleanMatches?: { [key: string]: boolean }
}

export type Select = {
    type: 'select'
    // Options displayed in Select component
    options: SelectOption[]
    alternateMatches: SelectOptionAlternates[]
}

export type SelectOption = {
    // UI-facing option label
    label: string
    // Field entry matching criteria as well as select output
    value: string
}

export type SelectOptionAlternates = {
    source: string
    target: string
}


export type Input = {
    type: 'input',
    options?: SelectOption[]
    alternateMatches?: SelectOptionAlternates[]
}

export type Validation = RequiredValidation | UniqueValidation | RegexValidation

export type RequiredValidation = {
    rule: 'required'
    errorMessage?: string
    level?: ErrorLevel
}

export type UniqueValidation = {
    rule: 'unique'
    allowEmpty?: boolean
    errorMessage?: string
    level?: ErrorLevel
}

export type RegexValidation = {
    rule: 'regex'
    value: string
    flags?: string
    errorMessage: string
    level?: ErrorLevel
}

export type RowHook<T extends string> = (
    row: Data<T>,
    addError: (fieldKey: T, error: Info) => void,
    table: Data<T>[],
) => Data<T>
export type TableHook<T extends string> = (
    table: Data<T>[],
    addError: (rowIndex: number, fieldKey: T, error: Info) => void,
) => Data<T>[]

export type ErrorLevel = 'info' | 'warning' | 'error'

export type Info = {
    message: string
    level: ErrorLevel
}

export type Result<T extends string> = {
    validData: Data<T>[]
    invalidData: Data<T>[]
    all: (Data<T> & Meta)[]
    matchedOptions: { column: string; entry: string; value: T; }[]
    matchedColumns: Column<T>[];
    chargeAttachments?: CustomFile[];
}
